'use client';

import * as ScrollArea from '@radix-ui/react-scroll-area';
import {
  Content,
  Item,
  ItemText,
  type SelectProps as RadixSelectProps,
  Root,
  Trigger,
  Value,
  Viewport,
} from '@radix-ui/react-select';
import clsx from 'clsx';
import { useField } from 'formik';
import { useTranslations } from 'next-intl';
import { type AriaAttributes, useRef, useState } from 'react';

import Icon from '@commons/components/Icon';
import Text from '@commons/components/Text';
import Flex from '@commons/containers/Flex';
import { useMediaQueries } from '@commons/contexts/responsive';
import type { Theme } from '@commons/types/styles';

import styles from './index.module.scss';

/**
 * Type definition for a select option.
 */

export type Option = {
  label: string;
  value: string;
};

/**
 * Type definition for the Select component properties.
 */

export type SelectProps = RadixSelectProps &
  Pick<AriaAttributes, 'aria-label' | 'aria-labelledby'> & {
    name: string;
    options: Option[];
    id?: string;
    theme?: Theme;
    error?: boolean;
    className?: string;
    placeholder?: string;
    searchable?: boolean;
    variant?: 'primary' | 'secondary';
  };

/**
 * Select component.
 *
 * This export can be used as an uncontrolled component or with a custom controller.
 */

export const Select = ({
  options,
  id,
  theme = 'white',
  error = false,
  className,
  placeholder,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
  variant = 'primary',
  required,
  searchable = false,
  ...props
}: SelectProps) => {
  const t = useTranslations('search');
  const [isOpen, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const { ready, minWidth } = useMediaQueries();

  const selectedValue = props.value ?? '';
  const inputRef = useRef<HTMLInputElement | null>(null);

  const triggerClasses = clsx(
    styles.trigger,
    styles[variant],
    error && styles.error,
    selectedValue && styles.filled,
    className
  );

  const contentClasses = clsx(styles.content, `theme-${theme}`);

  // Handle dropdown open state to reset the search term and refocus input
  const handleOpenChange = (open: boolean) => {
    if (open === false) {
      // Early return if the search input is still focused
      if (document.activeElement === inputRef.current) return;
    }
    setOpen(open);

    if (open) {
      setSearchTerm(''); // Reset search input when opening dropdown
      setTimeout(() => inputRef.current?.focus({ preventScroll: true }), 0); // Refocus input after dropdown opens
    }
  };

  // Handle input change and force refocus
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setTimeout(() => inputRef.current?.focus(), 0); // Ensure focus remains in input field
  };

  // Filter options based on search input
  const filteredOptions = options.filter((option) =>
    option.label.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Disable search until we know we're on a non-mobile device.
  const isMobile = !ready || !minWidth.md;
  const isSearchable = searchable && !isMobile;

  const renderedOptions =
    filteredOptions.length > 0 ? (
      filteredOptions.map(({ label, value }, index) => (
        <Item
          key={index}
          value={value}
          tabIndex={-1} // Prevent items from being focused
          className={styles.item}
          data-disabled={isSearchable}
          onKeyDown={(e) => {
            if (index === 0 && e.key === 'ArrowUp' && isSearchable) {
              e.preventDefault();
              inputRef.current?.focus();
            }
          }}
        >
          <ItemText asChild>
            <Text as="span" aria-label={value}>
              {label}
            </Text>
          </ItemText>
        </Item>
      ))
    ) : (
      <div className={styles.noResults}>
        <Text as="span">{t('noResults')}</Text>
      </div>
    );

  const asterisks = required ? ' *' : '';

  return (
    <Root open={isOpen} required={required} onOpenChange={handleOpenChange} {...props}>
      <Trigger
        id={id}
        className={triggerClasses}
        aria-label={ariaLabel ?? (ariaLabelledBy ? undefined : placeholder)}
        aria-labelledby={ariaLabelledBy}
      >
        <Flex direction="row" gap="xs" justify="space-between" align="center">
          <Value placeholder={`${placeholder}${asterisks}`} />
          <Icon type="chevronBottom" className={styles.icon} />
        </Flex>
      </Trigger>
      <Content position="popper" className={contentClasses}>
        <ScrollArea.Root className={styles.scrollAreaRoot} type="auto">
          <Viewport className={styles.viewport}>
            <ScrollArea.Viewport className={styles.scrollAreaViewport} asChild>
              <>
                {isSearchable && (
                  <input
                    ref={inputRef}
                    type="text"
                    placeholder={t('label')}
                    aria-label={t('label')}
                    className={styles.searchInput}
                    value={searchTerm}
                    onChange={handleInputChange}
                    onKeyDown={(e) => e.stopPropagation()}
                    onKeyUp={(e) => e.stopPropagation()}
                  />
                )}
                {renderedOptions}
              </>
            </ScrollArea.Viewport>
          </Viewport>
          <ScrollArea.Scrollbar className={styles.scrollAreaScrollbar} orientation="vertical">
            <ScrollArea.Thumb className={styles.scrollAreaThumb} />
          </ScrollArea.Scrollbar>
        </ScrollArea.Root>
      </Content>
    </Root>
  );
};

/**
 * Formik-controlled Select component.
 *
 * This export should be used inside a Formik form.
 */

export const FormikSelect = ({ name, ...props }: SelectProps) => {
  const [field, meta, helpers] = useField(name);

  const { touched, error } = meta;
  const { setValue } = helpers;

  const isError = touched && error !== undefined;

  return <Select onValueChange={setValue} error={isError} {...field} {...props} />;
};

export default Select;
